import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface SearchResult {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  image_url: string;
  category: string;
  author_name: string;
  author_avatar: string;
  created_at: string;
  relevance_score: number;
}

export interface SearchFilters {
  category?: string;
  author?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'relevance' | 'date' | 'popular';
}

export interface SearchSuggestion {
  suggestion: string;
  search_count: number;
  source: string;
  metadata?: {
    slug?: string;
    category?: string;
    author?: string;
    type?: string;
    article_count?: number;
    popularity?: number;
    profile_url?: string;
    category_url?: string;
    image_url?: string;
    avatar_url?: string | null;
  };
}

export const useSearch = () => {
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentQueryId, setCurrentQueryId] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>({
    sortBy: 'relevance'
  });

  const search = useCallback(async (
    searchQuery: string,
    searchFilters: SearchFilters = {},
    page: number = 0,
    limit: number = 20
  ) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setTotalCount(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const offset = page * limit;

      const { data, error: searchError } = await supabase.rpc('advanced_search', {
        search_query: searchQuery,
        search_limit: limit,
        search_offset: offset,
        category_filter: searchFilters.category || null,
        date_from: searchFilters.dateFrom || null,
        date_to: searchFilters.dateTo || null,
        author_filter: searchFilters.author || null,
        sort_by: searchFilters.sortBy || 'relevance'
      });

      if (searchError) throw searchError;

      if (data && data.length > 0) {
        setResults(data);
        setTotalCount(data[0].total_count || 0);

        const { data: queryId } = await supabase.rpc('track_search_query', {
          query_text: searchQuery,
          results_count: data[0].total_count || 0,
          user_id: user?.id || null,
          filters_json: searchFilters
        });

        if (queryId) {
          setCurrentQueryId(queryId);
        }
      } else {
        setResults([]);
        setTotalCount(0);

        await supabase.rpc('track_search_query', {
          query_text: searchQuery,
          results_count: 0,
          user_id: user?.id || null,
          filters_json: searchFilters
        });
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const getSuggestions = useCallback(async (prefix: string) => {
    if (!prefix.trim() || prefix.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const term = prefix.trim();
      const results: SearchSuggestion[] = [];

      const [articlesRes, authorsRes, profilesRes, categoriesRes] = await Promise.all([
        supabase
          .from('articles')
          .select('id, title, slug, category, author_name, image_url')
          .eq('status', 'published')
          .ilike('title', `%${term}%`)
          .limit(4),
        supabase
          .from('articles')
          .select('author_name')
          .eq('status', 'published')
          .ilike('author_name', `%${term}%`)
          .limit(3),
        supabase
          .from('profiles')
          .select('username, avatar_url')
          .ilike('username', `%${term}%`)
          .limit(3),
        supabase
          .from('articles')
          .select('category')
          .eq('status', 'published')
          .ilike('category', `%${term}%`)
          .limit(3),
      ]);

      const seenSuggestions = new Set<string>();

      (articlesRes.data || []).forEach((a) => {
        const key = a.title.toLowerCase();
        if (!seenSuggestions.has(key)) {
          seenSuggestions.add(key);
          results.push({
            suggestion: a.title,
            search_count: 0,
            source: 'article',
            metadata: {
              slug: a.slug,
              category: a.category,
              author: a.author_name,
              image_url: a.image_url,
            }
          });
        }
      });

      const seenAuthors = new Set<string>();
      const expandUsername = (username: string) =>
        username.replace(/([a-z])([A-Z])/g, '$1 $2');

      const profileList = profilesRes.data || [];
      const profileDetails = await Promise.all(
        profileList.map(async (p) => {
          const spacedName = expandUsername(p.username);
          const [byId, byName] = await Promise.all([
            supabase
              .from('articles')
              .select('id', { count: 'exact', head: true })
              .eq('status', 'published')
              .eq('author_id', p.id),
            supabase
              .from('articles')
              .select('id, author_avatar', { count: 'exact' })
              .eq('status', 'published')
              .or(`author_name.ilike.${spacedName},author_name.ilike.${p.username}`)
              .limit(1),
          ]);
          const articleCount = Math.max(byId.count || 0, byName.count || 0);
          const fallbackAvatar = byName.data?.[0]?.author_avatar || null;
          return {
            profile: p,
            articleCount,
            avatarUrl: p.avatar_url || fallbackAvatar,
          };
        })
      );

      profileDetails.forEach(({ profile, articleCount, avatarUrl }) => {
        const key = profile.username.toLowerCase();
        if (!seenAuthors.has(key)) {
          seenAuthors.add(key);
          results.push({
            suggestion: profile.username,
            search_count: articleCount,
            source: 'author',
            metadata: {
              type: 'writer',
              profile_url: `/writer/${profile.username}`,
              article_count: articleCount,
              avatar_url: avatarUrl,
            },
          });
        }
      });

      const authorNameCounts = new Map<string, number>();
      (authorsRes.data || []).forEach((a) => {
        if (!a.author_name) return;
        authorNameCounts.set(
          a.author_name,
          (authorNameCounts.get(a.author_name) || 0) + 1
        );
      });

      for (const [authorName, count] of authorNameCounts) {
        const key = authorName.toLowerCase();
        if (seenAuthors.has(key)) continue;
        const compactKey = authorName.replace(/\s+/g, '').toLowerCase();
        if (seenAuthors.has(compactKey)) continue;
        seenAuthors.add(key);

        const { data: sampleArticle } = await supabase
          .from('articles')
          .select('author_avatar')
          .eq('status', 'published')
          .eq('author_name', authorName)
          .limit(1)
          .maybeSingle();

        results.push({
          suggestion: authorName,
          search_count: count,
          source: 'author',
          metadata: {
            type: 'writer',
            article_count: count,
            avatar_url: sampleArticle?.author_avatar || null,
          },
        });
      }

      const seenCategories = new Set<string>();
      (categoriesRes.data || []).forEach((a) => {
        if (!a.category) return;
        const key = a.category.toLowerCase();
        if (!seenCategories.has(key)) {
          seenCategories.add(key);
          results.push({
            suggestion: a.category,
            search_count: 0,
            source: 'category',
            metadata: { type: 'category' }
          });
        }
      });

      if (results.length === 0) {
        const { data: fallback } = await supabase
          .from('articles')
          .select('id, title, slug, category, author_name, image_url')
          .eq('status', 'published')
          .order('created_at', { ascending: false })
          .limit(3);

        (fallback || []).forEach((a) => {
          results.push({
            suggestion: a.title,
            search_count: 0,
            source: 'article',
            metadata: {
              slug: a.slug,
              category: a.category,
              author: a.author_name,
              image_url: a.image_url,
            }
          });
        });
      }

      setSuggestions(results.slice(0, 10));
    } catch (err) {
      console.error('Suggestions error:', err);
      setSuggestions([]);
    }
  }, []);

  const trackClick = useCallback(async (articleId: string) => {
    if (!currentQueryId) return;

    try {
      await supabase.rpc('track_search_click', {
        query_id_param: currentQueryId,
        article_id_param: articleId
      });
    } catch (err) {
      console.error('Track click error:', err);
    }
  }, [currentQueryId]);

  const clearResults = useCallback(() => {
    setResults([]);
    setTotalCount(0);
    setQuery('');
    setSuggestions([]);
    setCurrentQueryId(null);
  }, []);

  useEffect(() => {
    if (query.length >= 2) {
      getSuggestions(query);
    } else {
      setSuggestions([]);
    }
  }, [query, getSuggestions]);

  return {
    query,
    setQuery,
    results,
    suggestions,
    totalCount,
    loading,
    error,
    filters,
    setFilters,
    search,
    getSuggestions,
    trackClick,
    clearResults
  };
};
